import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getAllUsers } from "@/services/userService";
import { UserRole } from "@/types/user";
import { queryKeys } from "@/lib/queryKeys";
import { UserCheck, Calendar, Pencil, Info } from "lucide-react";

/** Opciones de plazo para calendarios de garantía (alineado con shared VehicleWarranty). */
const WARRANTY_PERIOD_OPTIONS = [
  { value: "1_mes", label: "1 mes" },
  { value: "3_meses", label: "3 meses" },
  { value: "6_meses", label: "6 meses" },
  { value: "1_año", label: "1 año" },
  { value: "2_años", label: "2 años" },
  { value: "extendida", label: "Garantía extendida" },
];

export function Garantias() {
  const { data: users = [], isLoading: loadingUsers } = useQuery({
    queryKey: queryKeys.users,
    queryFn: getAllUsers,
  });

  const guaranteers = users.filter((u) => u.role === UserRole.GUARANTEER);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold">Garantías</h1>
        <p className="text-muted-foreground">
          Garantizadores y calendarios de garantía. La configuración del módulo está en Configuración.
        </p>
      </div>

      <Tabs defaultValue="garantizador" className="space-y-4">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="garantizador" className="gap-2">
            <UserCheck className="h-4 w-4" />
            Garantizador
          </TabsTrigger>
          <TabsTrigger value="calendarios" className="gap-2">
            <Calendar className="h-4 w-4" />
            Calendarios
          </TabsTrigger>
        </TabsList>

        <TabsContent value="garantizador" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Garantizadores</CardTitle>
              <CardDescription>
                Usuarios con rol &quot;Garantizador&quot; que pueden gestionar y validar garantías.
                Asigna el rol desde la página de Usuarios.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingUsers ? (
                <p className="text-muted-foreground">Cargando usuarios…</p>
              ) : guaranteers.length === 0 ? (
                <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed p-8 text-center text-muted-foreground">
                  <UserCheck className="h-10 w-10 opacity-50" />
                  <p>No hay usuarios con rol Garantizador.</p>
                  <Button asChild variant="outline">
                    <Link to="/users">Ir a Usuarios</Link>
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Teléfono</TableHead>
                      <TableHead className="w-[100px]">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {guaranteers.map((u) => (
                      <TableRow key={u.id}>
                        <TableCell className="font-medium">
                          {[u.firstName, u.lastName].filter(Boolean).join(" ") || u.displayName || "—"}
                        </TableCell>
                        <TableCell>{u.email ?? "—"}</TableCell>
                        <TableCell>{u.phone ?? "—"}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/users/edit/${u.id}`}>
                              <Pencil className="h-4 w-4" />
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {users.length > 0 && guaranteers.length > 0 && (
                <div className="mt-4">
                  <Button asChild variant="outline" size="sm">
                    <Link to="/users">Ver todos los usuarios</Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="calendarios" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Calendarios de garantía</CardTitle>
              <CardDescription>
                Plazos de garantía disponibles para asignar a vehículos y repuestos. Estos valores
                se muestran en los formularios al elegir la garantía.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Plazo</TableHead>
                      <TableHead>Descripción</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {WARRANTY_PERIOD_OPTIONS.map((opt) => (
                      <TableRow key={opt.value}>
                        <TableCell className="font-medium">{opt.label}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {opt.value === "extendida"
                            ? "Garantía extendida según contrato"
                            : `Garantía estándar de ${opt.label}`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                <Info className="h-4 w-4 shrink-0" />
                Los plazos se aplican al crear o editar vehículos en el campo &quot;Garantía&quot;.
                La persistencia de calendarios personalizados se podrá configurar más adelante.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
